/**
 * Catalog name/price resolution for single-country vs merged pinned-country lists.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { Gateway, LeadsList } from '@/hooks';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

export function catalogCompositeKey(countryKey: string, numericId: number): string {
  return `${countryKey}::${numericId}`;
}

export type GatewayWithCountry = Gateway & { countryKey: string };
export type LeadsListWithCountry = LeadsList & { countryKey: string };

export interface CatalogResolvers {
  gatewayName: (d: PreparedDrop) => string;
  leadsListName: (d: PreparedDrop) => string;
  gatewayPricePerSms: (d: PreparedDrop) => number;
}

export function buildCatalogResolvers(
  countryScopedCatalog: boolean,
  gateways: Gateway[],
  leadsLists: LeadsList[],
  gatewaysWithCountry: GatewayWithCountry[],
  leadsWithCountry: LeadsListWithCountry[],
): CatalogResolvers {
  if (!countryScopedCatalog) {
    const gwName = new Map(gateways.map((g) => [g.id, g.name]));
    const llName = new Map(leadsLists.map((l) => [l.id, l.name]));
    const gwPrice = new Map(gateways.map((g) => [g.id, g.pricePerSms ?? 0]));
    return {
      gatewayName: (d) => gwName.get(d.routeId) ?? '',
      leadsListName: (d) => llName.get(d.leadsListWiserId) ?? '',
      gatewayPricePerSms: (d) => Number(gwPrice.get(d.routeId) ?? 0),
    };
  }
  const gName = new Map(
    gatewaysWithCountry.map((g) => [catalogCompositeKey(g.countryKey, g.id), g.name]),
  );
  const lName = new Map(
    leadsWithCountry.map((l) => [catalogCompositeKey(l.countryKey, l.id), l.name]),
  );
  const gPrice = new Map(
    gatewaysWithCountry.map((g) => [catalogCompositeKey(g.countryKey, g.id), g.pricePerSms ?? 0]),
  );
  return {
    gatewayName: (d) => gName.get(catalogCompositeKey(d.countryKey, d.routeId)) ?? '',
    leadsListName: (d) =>
      lName.get(catalogCompositeKey(d.countryKey, d.leadsListWiserId)) ?? '',
    gatewayPricePerSms: (d) =>
      Number(gPrice.get(catalogCompositeKey(d.countryKey, d.routeId)) ?? 0),
  };
}

export function buildCompositeTagsByLeadsWiserId(
  entries: Array<{ countryKey: string; tagsByWiserId?: Record<number, string[]> }>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const { countryKey, tagsByWiserId } of entries) {
    if (!tagsByWiserId) continue;
    for (const [wid, tags] of Object.entries(tagsByWiserId)) {
      out[catalogCompositeKey(countryKey, Number(wid))] = tags;
    }
  }
  return out;
}
